/* eslint-disable react-refresh/only-export-components --
   Route-manifest module: its export is the `RouteObject[]` table, not a
   component, so Fast Refresh doesn't apply here. */
import { lazy } from 'react'
import type { RouteObject } from 'react-router-dom'
import { AppShell } from '@components/shell/AppShell'
import { DetailShell } from '@components/shell/DetailShell'
import { AuthGuard } from '../auth/AuthGuard'
import NotFoundPage from '@pages/NotFound/NotFoundPage'
import { appRoutes, type AppRouteConfig } from './config'

// DEV-only Showroom route. Lazy-loaded outside `appRoutes` because it
// has no shell, no auth, and isn't part of the production surface.
const Showroom = lazy(() => import('@pages/_dev/Showroom'))

/**
 * Materialise a single `AppRouteConfig` entry into a React Router
 * `RouteObject`. The page component is lazy via `React.lazy` and the
 * route handle carries the full config so layouts can read title,
 * navItem, parentPath, etc. via `useMatches()`.
 */
function toRouteObject(route: AppRouteConfig): RouteObject {
  const Component = route.component
  return {
    path: route.path,
    element: <Component />,
    handle: route,
  }
}

function pickRoutes(predicate: (r: AppRouteConfig) => boolean): RouteObject[] {
  return appRoutes.filter(predicate).map(toRouteObject)
}

const publicRoutes = pickRoutes((r) => !r.requiresAuth)

const protectedAppShellRoutes = pickRoutes(
  (r) => r.requiresAuth && r.shell === 'app',
)

const protectedDetailShellRoutes = pickRoutes(
  (r) => r.requiresAuth && r.shell === 'detail',
)

const protectedNoShellRoutes = pickRoutes(
  (r) => r.requiresAuth && r.shell === 'none',
)

export const routes: RouteObject[] = [
  ...publicRoutes,

  // ------------------------------------------------------------------------
  // Authenticated routes — wrap everything in AuthGuard, then split by shell
  // ------------------------------------------------------------------------
  {
    element: <AuthGuard />,
    children: [
      {
        element: <AppShell />,
        children: protectedAppShellRoutes,
      },
      {
        element: <DetailShell />,
        children: protectedDetailShellRoutes,
      },
      // Shell-less protected routes (none today; left for future use).
      ...protectedNoShellRoutes,
    ],
  },

  // ------------------------------------------------------------------------
  // DEV-only Showroom — lives outside the app shell so the entire shell
  // doesn't cover the showroom layout.
  // ------------------------------------------------------------------------
  ...(import.meta.env.DEV
    ? [
        {
          path: '/dev/showroom',
          element: <Showroom />,
        } satisfies RouteObject,
      ]
    : []),

  // ------------------------------------------------------------------------
  // SPA catch-all — any URL matching no route above renders the 404 page.
  // Statically imported (not lazy) so the error page can never itself fail
  // to load. Kept outside the shells/AuthGuard so it renders chrome-free.
  // ------------------------------------------------------------------------
  {
    path: '*',
    element: <NotFoundPage />,
  },
]
